import { useCallback } from "react";
import { Banner, IconButton, Label } from "@hireart/component-library";
import trashIcon from "hireart-icons/trash-1.svg";
import PropTypes from "prop-types";
import {
  formatDurationFromHours,
  formatNextDayInfo,
  isOvernightTime,
  BreakShape,
} from "common/utils/timesheet";
import { TimePicker } from "common-components";

const getOvernightBreakNextDay = (breakItem, shiftDate) => {
  const relevantTime = breakItem.endTime ?? breakItem.startTime;
  if (!shiftDate || !isOvernightTime(relevantTime)) return null;
  return formatNextDayInfo(shiftDate);
};

export const ShiftBreaks = ({
  breaks,
  isReadOnly,
  hoursBreakEmployee = 0,
  shiftDate,
  onBreakChange,
  onRemoveBreak,
}) => {
  // Move useCallback before any conditional returns (rules of hooks)
  const handleTimeChange = useCallback(
    (breakIndex, field) => (time) => {
      onBreakChange(breakIndex, field, time);
    },
    [onBreakChange]
  );

  if (breaks.length === 0 && !hoursBreakEmployee) {
    return null;
  }

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex flex-col gap-1">
        <Label isUnstyled isOptional>
          Breaks (unpaid)
        </Label>
      </div>

      {breaks.length > 0 && (
        <div className="space-y-8">
          {breaks.map((breakItem, index) => {
            const overnightBreakNextDay = getOvernightBreakNextDay(
              breakItem,
              shiftDate
            );

            return (
              <div
                key={breakItem.id || `temp-${index}`}
                className="bg-background flex w-full flex-col gap-4 rounded-8 border border-neutral-40 p-12"
              >
                {/* First row: Start and end times */}
                <div className="flex w-full flex-col gap-4">
                  <TimePicker
                    label="Start"
                    value={breakItem.startTime}
                    onChange={handleTimeChange(index, "startTime")}
                    isDisabled={isReadOnly}
                  />

                  <TimePicker
                    label="End"
                    value={breakItem.endTime}
                    onChange={handleTimeChange(index, "endTime")}
                    isDisabled={isReadOnly}
                  />
                </div>

                {/* Overnight break notification */}
                {overnightBreakNextDay && (
                  <Banner variant="warning">
                    <div className="body-2 mb-1 font-semibold">
                      Overnight break
                    </div>
                    <div className="body-2">
                      This break ends on {overnightBreakNextDay.dayName},{" "}
                      {overnightBreakNextDay.month} {overnightBreakNextDay.day}
                    </div>
                  </Banner>
                )}

                {/* Second row: Remove break button */}
                {!isReadOnly && (
                  <div className="flex w-full justify-end">
                    <IconButton
                      onClick={() => onRemoveBreak(index)}
                      icon={trashIcon}
                      size="sm"
                      aria-label="Remove break"
                      color="red"
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {breaks.length === 0 && hoursBreakEmployee && isReadOnly && (
        <div className="bg-background flex w-full flex-col gap-4 rounded-8 border border-neutral-40 p-12">
          <div className="flex items-center gap-2">
            <span className="font-medium">
              Break Duration: {formatDurationFromHours(hoursBreakEmployee)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

ShiftBreaks.propTypes = {
  breaks: PropTypes.arrayOf(BreakShape).isRequired,
  isReadOnly: PropTypes.bool.isRequired,
  hoursBreakEmployee: PropTypes.number,
  shiftDate: PropTypes.instanceOf(Date),
  onBreakChange: PropTypes.func.isRequired,
  onRemoveBreak: PropTypes.func.isRequired,
};
